/**
 * Super Admin — AI Debug Trace Viewer
 *
 * Read-only inspector for the Section A deterministic engine traces.
 * Backend: /api/content-engine/admin/ai-debug-logs* (super_admin gated).
 *
 * Mounted at /super-admin/ai-debug-logs and protected by ProtectedRoute(['super_admin']).
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Activity, AlertCircle, CheckCircle2, ChevronRight, Clock,
  Database, Filter, Loader2, RefreshCw, Search, X, Zap,
} from 'lucide-react';
import apiService from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

const TASK_TYPES = [
  'Assignment', 'Test', 'Exam', 'Homework', 'Holiday Homework',
  'Debate', 'Play / Skit', 'Book / Chapter Task', 'Summary', 'Custom',
];

const STATUS_BADGE = {
  valid:    { label: 'Valid',    cls: 'bg-emerald-100 text-emerald-800 border-emerald-300', icon: CheckCircle2 },
  partial:  { label: 'Partial',  cls: 'bg-amber-100 text-amber-800 border-amber-300',       icon: AlertCircle },
  fallback: { label: 'Fallback', cls: 'bg-rose-100 text-rose-800 border-rose-300',          icon: AlertCircle },
};

function StatusBadge({ status }) {
  const conf = STATUS_BADGE[status] || { label: status || 'unknown', cls: 'bg-slate-100 text-slate-700', icon: AlertCircle };
  const Ico = conf.icon;
  return (
    <Badge variant="outline" className={`gap-1 ${conf.cls}`} data-testid={`status-${status}`}>
      <Ico className="h-3 w-3" /> {conf.label}
    </Badge>
  );
}

function StatCard({ label, value, sub, icon: Icon, testId }) {
  return (
    <Card className="p-4 flex items-center gap-3" data-testid={testId}>
      <div className="h-10 w-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center text-indigo-700 dark:text-indigo-300">
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">{label}</div>
        <div className="text-xl font-bold tabular-nums">{value}</div>
        {sub && <div className="text-[11px] text-muted-foreground truncate">{sub}</div>}
      </div>
    </Card>
  );
}

export default function AiDebugLogsPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);
  const [skip, setSkip] = useState(0);
  const limit = 25;

  // filters
  const [taskType, setTaskType] = useState('');
  const [status, setStatus] = useState('');
  const [searchTrace, setSearchTrace] = useState('');

  // detail drawer
  const [activeTrace, setActiveTrace] = useState(null);
  const [activeLoading, setActiveLoading] = useState(false);

  const loadList = useCallback(async (resetSkip = false) => {
    setRefreshing(true); setError('');
    try {
      const useSkip = resetSkip ? 0 : skip;
      const params = new URLSearchParams({ limit: String(limit), skip: String(useSkip) });
      if (taskType) params.set('taskType', taskType);
      if (status) params.set('status', status);
      const data = await apiService.request(`/content-engine/admin/ai-debug-logs?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
      if (resetSkip) setSkip(0);
    } catch (e) {
      setError(e.message || 'Failed to load traces');
    } finally {
      setLoading(false); setRefreshing(false);
    }
  }, [skip, taskType, status]);

  const loadStats = useCallback(async () => {
    try {
      const s = await apiService.request('/content-engine/admin/ai-debug-logs/stats/summary');
      setStats(s);
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => { loadList(false); }, [loadList]);
  useEffect(() => { loadStats(); }, [loadStats]);

  // when filters change, reset pagination
  useEffect(() => { loadList(true); /* eslint-disable-line */ }, [taskType, status]);

  const openTrace = async (traceId) => {
    setActiveLoading(true);
    setActiveTrace({ traceId, _loading: true });
    try {
      const full = await apiService.request(`/content-engine/admin/ai-debug-logs/${encodeURIComponent(traceId)}`);
      setActiveTrace(full);
    } catch (e) {
      setActiveTrace({ traceId, _error: e.message || 'Failed to load trace' });
    } finally {
      setActiveLoading(false);
    }
  };

  const filteredItems = useMemo(() => {
    if (!searchTrace.trim()) return items;
    const q = searchTrace.trim().toLowerCase();
    return items.filter(it =>
      it.traceId?.toLowerCase().includes(q) ||
      it.userId?.toLowerCase().includes(q) ||
      it.taskType?.toLowerCase().includes(q),
    );
  }, [items, searchTrace]);

  return (
    <div className="p-6 space-y-6" data-testid="ai-debug-logs-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Database className="h-6 w-6 text-indigo-600" />
            AI Debug Trace Viewer
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Section A deterministic engine — full per-call traceability. Read-only.
          </p>
        </div>
        <Button
          variant="outline" size="sm" onClick={() => { loadList(true); loadStats(); }}
          disabled={refreshing} data-testid="refresh-btn"
        >
          {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          <span className="ml-2">Refresh</span>
        </Button>
      </div>

      {/* Stats strip */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="stats-strip">
          <StatCard label="Total traces" value={stats.total ?? 0} icon={Activity} testId="stat-total" />
          <StatCard
            label="Valid / Partial / Fallback"
            value={`${stats.byStatus?.valid || 0} / ${stats.byStatus?.partial || 0} / ${stats.byStatus?.fallback || 0}`}
            icon={CheckCircle2}
            testId="stat-status"
          />
          <StatCard
            label="Avg Duration"
            value={`${stats.avgDurationMs || 0} ms`}
            sub={`peak ${stats.maxDurationMs || 0} ms`}
            icon={Clock}
            testId="stat-duration"
          />
          <StatCard
            label="Top task"
            value={stats.byTaskType?.[0]?.taskType || '—'}
            sub={stats.byTaskType?.[0] ? `${stats.byTaskType[0].count} traces` : ''}
            icon={Zap}
            testId="stat-top-task"
          />
        </div>
      )}

      {/* Filter bar */}
      <Card className="p-3 flex flex-wrap gap-2 items-center" data-testid="filter-bar">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <Select value={taskType || 'all'} onValueChange={(v) => setTaskType(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-44" data-testid="filter-task-type"><SelectValue placeholder="Task type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All task types</SelectItem>
            {TASK_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status || 'all'} onValueChange={(v) => setStatus(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-36" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="valid">Valid</SelectItem>
            <SelectItem value="partial">Partial</SelectItem>
            <SelectItem value="fallback">Fallback</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={searchTrace} onChange={(e) => setSearchTrace(e.target.value)}
            placeholder="Search trace ID / user / task…"
            className="pl-8" data-testid="filter-search"
          />
        </div>
        <Badge variant="secondary" data-testid="result-count">{total} total</Badge>
      </Card>

      {/* Error banner */}
      {error && (
        <Card className="p-3 border-rose-300 bg-rose-50 text-rose-800 flex items-center gap-2" data-testid="error-banner">
          <AlertCircle className="h-4 w-4" /> {error}
        </Card>
      )}

      {/* Trace list */}
      <Card className="overflow-hidden" data-testid="trace-list">
        {loading ? (
          <div className="p-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : filteredItems.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground" data-testid="empty-state">
            <Database className="h-10 w-10 mx-auto mb-2 opacity-30" />
            <div className="font-medium">No traces yet</div>
            <div className="text-xs mt-1">Generate Section A content to populate this log.</div>
          </div>
        ) : (
          <div className="divide-y divide-border">
            <div className="grid grid-cols-12 gap-2 px-4 py-2 text-[11px] uppercase tracking-wide font-semibold text-muted-foreground bg-muted/40">
              <div className="col-span-3">Trace ID</div>
              <div className="col-span-2">Task / Doc</div>
              <div className="col-span-2">User</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-2">Duration</div>
              <div className="col-span-1"></div>
            </div>
            {filteredItems.map((it) => (
              <button
                key={it.traceId}
                onClick={() => openTrace(it.traceId)}
                className="w-full grid grid-cols-12 gap-2 px-4 py-3 text-left hover:bg-accent/40 transition-colors items-center"
                data-testid={`trace-row-${it.traceId}`}
              >
                <div className="col-span-3 font-mono text-xs truncate">{it.traceId}</div>
                <div className="col-span-2 text-sm truncate">
                  <div className="font-medium">{it.taskType}</div>
                  <div className="text-[11px] text-muted-foreground">{it.docType}</div>
                </div>
                <div className="col-span-2 font-mono text-[11px] text-muted-foreground truncate">{it.userId || '—'}</div>
                <div className="col-span-2"><StatusBadge status={it.status} /></div>
                <div className="col-span-2 text-sm tabular-nums">{it.durationMs ?? 0} ms</div>
                <div className="col-span-1 text-right text-muted-foreground"><ChevronRight className="h-4 w-4 inline" /></div>
              </button>
            ))}
          </div>
        )}
        {/* Pagination */}
        {total > limit && (
          <div className="p-3 flex justify-between items-center border-t border-border" data-testid="pagination">
            <div className="text-xs text-muted-foreground">Showing {skip + 1}–{Math.min(skip + limit, total)} of {total}</div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={skip === 0} onClick={() => { setSkip(Math.max(0, skip - limit)); setTimeout(() => loadList(false), 0); }}>Prev</Button>
              <Button size="sm" variant="outline" disabled={skip + limit >= total} onClick={() => { setSkip(skip + limit); setTimeout(() => loadList(false), 0); }}>Next</Button>
            </div>
          </div>
        )}
      </Card>

      {/* Detail Drawer */}
      <Sheet open={!!activeTrace} onOpenChange={(o) => { if (!o) setActiveTrace(null); }}>
        <SheetContent side="right" className="sm:max-w-2xl w-full overflow-y-auto" data-testid="trace-detail-drawer">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Activity className="h-4 w-4" /> Trace {activeTrace?.traceId?.slice(-12)}
            </SheetTitle>
            <SheetDescription>
              {activeTrace?.taskType} → {activeTrace?.docType}
              {activeTrace?.status && <span className="ml-2"><StatusBadge status={activeTrace.status} /></span>}
            </SheetDescription>
          </SheetHeader>
          {activeLoading || activeTrace?._loading ? (
            <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : activeTrace?._error ? (
            <div className="p-4 text-rose-700 text-sm">{activeTrace._error}</div>
          ) : activeTrace ? (
            <TraceDetail trace={activeTrace} />
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Section({ title, children, testId }) {
  return (
    <div className="mt-4" data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground mb-1">{title}</div>
      {children}
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div className="flex gap-2 text-sm py-0.5">
      <div className="text-muted-foreground min-w-[110px]">{k}</div>
      <div className="font-mono text-xs break-all">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</div>
    </div>
  );
}

function TraceDetail({ trace }) {
  const validation = trace.steps?.find(s => s.step?.startsWith('validate_'));
  return (
    <div className="space-y-1">
      <Section title="Meta" testId="trace-meta">
        <KV k="Created" v={trace.createdAt} />
        <KV k="User" v={trace.userId || '—'} />
        <KV k="Duration" v={`${trace.durationMs} ms`} />
        <KV k="Blueprint" v={trace.blueprint} />
      </Section>

      <Section title="Raw UI input" testId="trace-rawui">
        <pre className="text-[11px] bg-muted/40 rounded p-2 overflow-x-auto">{JSON.stringify(trace.rawUI, null, 2)}</pre>
      </Section>

      <Section title="Pipeline steps" testId="trace-steps">
        <div className="space-y-1">
          {(trace.steps || []).map((s, i) => (
            <div key={i} className="flex gap-2 items-start text-xs border-l-2 border-indigo-300 pl-2">
              <span className="font-mono text-muted-foreground tabular-nums w-12 shrink-0">{(s.ts || 0).toFixed(2)}s</span>
              <span className="font-semibold">{s.step}</span>
              {s.ok !== undefined && (s.ok
                ? <CheckCircle2 className="h-3 w-3 text-emerald-600 mt-0.5" />
                : <AlertCircle className="h-3 w-3 text-amber-600 mt-0.5" />)}
              {s.errors?.length > 0 && (
                <span className="text-rose-700">— {s.errors.join('; ')}</span>
              )}
              {s.error && <span className="text-rose-700 truncate">— {s.error}</span>}
            </div>
          ))}
        </div>
      </Section>

      {validation && (
        <Section title="Final validation" testId="trace-validation">
          {validation.ok ? (
            <Badge variant="outline" className="gap-1 bg-emerald-100 text-emerald-800 border-emerald-300">
              <CheckCircle2 className="h-3 w-3" /> Passed (no violations)
            </Badge>
          ) : (
            <ul className="text-xs text-rose-700 list-disc ml-4">
              {(validation.errors || []).map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          )}
        </Section>
      )}

      <Section title="Final output (truncated to 25 KB on store)" testId="trace-output">
        <pre className="text-[11px] bg-muted/40 rounded p-2 overflow-x-auto max-h-96">
          {JSON.stringify(trace.finalOutput, null, 2)}
        </pre>
      </Section>
    </div>
  );
}
